


import { Component, Vue } from "vue-property-decorator";

import { CustomError, CustomNotification } from "@Internal/Models";

import { Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import {
    Newsletter, NewsletterCount, NewsletterSummary
} from "App/Models/Newsletter";

import BooleanField from "Web/Components/Common/Input/Boolean.vue";
import Icon from "Web/Components/Icon.vue";

import NewsletterForm from "./Form.vue";
import NewsletterStats from "./Stats.vue";
import NewsletterTest from "./Test.vue";
import { DEMO_COUNT, DemoData, IsDemo, LoadDemo } from "./Demo";
import { DescribeFrequency, ErrorMessage, FormatDate, FormatNumber, FormatRate } from "./Helpers";

/* LES STATISTIQUES D'UNE NEWSLETTER, SUR UN ECRAN A ELLES — meme ecran que le
   Desktop ( Fred, 22/10/2026 : « un ecran completement a part, avec dans l'entete
   les grandes lignes de la newsletter et en dessous les statistiques » ).

   L'entete : retour a la liste du client, nom, interrupteur, Regler. Puis le
   resume de la newsletter, et les statistiques dessous ( `Stats.vue` ). Pas de
   « Modifier l'e-mail » : il se construit dans le PB e-mail du client.

   ⚠️ Un getter ne s'appelle jamais comme un crochet de Vue ( `updated`, `created`… ) :
   vue-class-component le lit au chargement du module, sans instance, et l'ecran
   entier plante ( vecu sur le Desktop le 22/10/2026 ). */
@Component({
    components: {
        BooleanField,
        Icon,
        NewsletterForm,
        NewsletterStats,
        NewsletterTest
    }
})
export default class NewsletterStatsPage extends Vue {
    private record: Newsletter | null = null;
    private count: NewsletterCount | null = null;
    /* Les chiffres depuis le premier envoi, pour le bloc des e-mails ouverts. */
    private summary: NewsletterSummary | null = null;
    /* Les donnees de demonstration ( `?demo=1`, hors production ) : `Demo.ts`. */
    private demo: DemoData | null = null;

    private editing: boolean = false;
    private testing: boolean = false;
    private testAnchor: { top: number; right: number } | null = null;

    async created(): Promise<void> {
        await this.load();
    }

    private get groupId(): number {
        return Number( this.$route.params.groupId );
    }

    private get id(): number {
        return Number( this.$route.params.id );
    }

    private async load(): Promise<void> {
        const [ record, error ] = await Newsletters.Read( this.groupId, this.id );

        if( error !== undefined || record === undefined ) {
            this.fail( error );
            return;
        }

        this.record = record;
        await this.loadRecipients();
    }

    /* Les contacts eligibles et les chiffres depuis le premier envoi. */
    private async loadRecipients(): Promise<void> {
        if( this.record === null )
            return;

        const [ [ count ], [ summary ] ] = await Promise.all( [
            Newsletters.Count( this.groupId, this.record.search ),
            Newsletters.Stats( this.groupId, this.record.id )
        ] );

        this.count = count || null;
        this.summary = summary || null;

        if( IsDemo( this.$route.query ) ) {
            this.demo = await LoadDemo(
                this.groupId,
                this.count !== null ? this.count.total : 0,
                this.record.next_send_at
            );
            this.summary = this.demo.summary();
            this.count = DEMO_COUNT;
        }
    }

    private fail( error: CustomError | undefined ): void {
        if( error === undefined )
            return;

        EventBus.Publish(
            "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
        );
    }

    /* L'e-mail se construit depuis la Console des que l'API le lit ( brief du
       26/10/2026 ) : sans `content_editor` dans la lecture, le bouton reste grise. */
    private get emailReady(): boolean {
        return this.record !== null && this.record.content_editor !== undefined;
    }

    private editEmail(): void {
        if( !this.emailReady )
            return;

        this.$router.push( {
            name: "newsletters-email",
            params: { groupId: String( this.groupId ), id: String( this.id ) },
            query: this.$route.query
        } );
    }

    /* Retour a la liste du client, avec son nom et son e-mail ( dans l'adresse ). */
    private back(): void {
        this.$router.push( {
            name: "newsletters-group",
            params: { groupId: String( this.groupId ) },
            query: this.$route.query
        } );
    }

    private async toggleActive( value: boolean ): Promise<void> {
        if( this.record === null )
            return;

        const [ updated, error ] = await Newsletters.Update( this.groupId, this.record.id, {
            active: value
        } );

        if( error !== undefined || updated === undefined ) {
            this.fail( error );
            /* L'interrupteur revient a la valeur relue. */
            await this.load();
            return;
        }

        this.record = updated;
    }

    private async onSaved( newsletter: Newsletter ): Promise<void> {
        this.record = newsletter;
        this.editing = false;

        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_title" ), $t( "NEWSLETTERS_saved" ).toString()
        ) );

        await this.loadRecipients();
    }

    private openTestFrom( button: DOMRect ): void {
        this.testAnchor = {
            top: Math.round( button.bottom + 8 ),
            right: Math.round( window.innerWidth - button.right )
        };
        this.testing = true;
    }

    private get warning(): string {
        if( this.record === null )
            return "";

        if( !this.record.has_dynamic_block )
            return $t( "NEWSLETTERS_noDynamicBlock" ).toString();

        if( !this.record.sender_email )
            return $t( "NEWSLETTERS_noSenderWarning" ).toString();

        return "";
    }

    private get sender(): string {
        if( this.record === null || !this.record.sender_email )
            return $t( "NEWSLETTERS_noSender" ).toString();

        /* L'adresse seule, sans le nom ( Fred, 22/10/2026 ). */
        return this.record.sender_email;
    }

    /* « 139 ecartes : 138 sans adresse e-mail, 1 sans autorisation (RGPD) » — sans
       les raisons a zero. */
    /* LES ECARTES EN TABLEAU ( Fred, 27/10/2026 : « toute cette partie sous le
       chiffre, c'est un peu le bazar » ) : un motif par ligne, le nombre a droite,
       sans les motifs a zero. */
    private get excludedRows(): { key: string; label: string; value: string }[] {
        if( this.count === null )
            return [];

        const excluded = this.count.excluded;

        return ( [
            [ "no_email", excluded.no_email, "NEWSLETTERS_excludedNoEmail" ],
            [ "not_authorized", excluded.not_authorized, "NEWSLETTERS_excludedNotAuthorized" ],
            [ "bounced", excluded.bounced, "NEWSLETTERS_excludedBounced" ]
        ] as [ string, number, string ][] )
            .filter( ( [ , value ] ) => value > 0 )
            .map( ( [ key, value, label ] ) => ( {
                key,
                label: $t( label ).toString(),
                value: FormatNumber( value )
            } ) );
    }

    private get excludedTotal(): string {
        return this.count === null
            ? ""
            : FormatNumber( Math.max( 0, this.count.matched - this.count.total ) );
    }

    /* L'adresse de reponse, seulement quand elle n'est pas celle de l'expediteur. */
    private get replyTo(): string {
        if( this.record === null || !this.record.reply_to_email )
            return "";

        return ( this.record.reply_to_email === this.record.sender_email )
            ? ""
            : this.record.reply_to_email;
    }

    private get nextSend(): string {
        if( this.record === null )
            return "";

        return this.record.active
            ? FormatDate( this.record.next_send_at )
            : $t( "NEWSLETTERS_paused" ).toString();
    }

    private get frequency(): string {
        return ( this.record !== null ) ? DescribeFrequency( this.record.frequency ) : "";
    }


    private num( value: number | null | undefined ): string {
        return FormatNumber( value );
    }

    private rate( value: number | null | undefined ): string {
        return FormatRate( value );
    }

}
